import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  setListingQueryParams,
  TListingQueryParams,
} from "../../api/store/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLoggedInUserData } from "../../utils/user";
import { useDebouncedCallback } from "use-debounce";
import {
  useOrganizationVehicleAlertsQuery,
  useSingleOrganizationVehicleAlertQuery,
  useOrganizationVehiclesQuery,
} from "../../api/network/adminApiServices";
import {
  SingleOrganizationVehicleAlertPayload,
  TLegacyAlert,
  TLegacySingleAlert,
} from "../../api/types/Alert";
import { TListData } from "./type";
import HeaderView from "../../components/admin/headerView";
import { routeUrls } from "../../navigation/routeUrls";
import { APP_CONFIG } from "../../constants/constants";
import AppSearchBox from "../../components/searchBox";
import AdminListingColumnItem from "../../components/adminListingColumnItem";
import Pagination, { TPaginationSelected } from "../../components/pagination";
import { TSelectboxOption } from "../../components/admin/formFields";
import AdminTable from "../../components/adminTable";
import { TAdminTableRowData } from "../../components/types";
import React, { useEffect } from "react";
import { dbTimeToJsTime } from "../../utils/dateTime";
import { consistentDisplayAssetItem } from "../../utils/vehicle";

const all_columns = [
  "Asset Id",
  "Description",
  "Alert Status",
  "Alert Rule",
  "Date and Time",
];

const ScreenReportAlertDetails = () => {
  const { vehicleId } = useParams<{ vehicleId: any }>();
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "vehicleAlerts.detailsPage",
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const {
    vehicleAlerts: orgVehicleAlertsQueryParams,
    vehicleAlertsSingle: singleVehicleAlertsQueryParams,
  } = listingQueryParams;

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const { data: allVehiclesData } = useOrganizationVehiclesQuery({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
  });

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        vehicleAlerts: {
          ...orgVehicleAlertsQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  // fetch organization vehicle alerts
  const {
    data: dataOrgVehicleAlerts,
    isFetching: isFetchingOrgVehicleAlerts,
    error,
  } = useOrganizationVehicleAlertsQuery(orgVehicleAlertsQueryParams);
  const { results, count } = dataOrgVehicleAlerts || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgVehicleAlerts]);

  // prepare list data for vehicle alert list
  const listData: TListData[] = !!results
    ? (results || ([] as TLegacyAlert[])).map(
        (item: TLegacyAlert, index: number) => ({
          device_id: item?.deviceid || "-",
          description: item?.rule?.description || "-",
          last_alert: new Date(
            dbTimeToJsTime(item?.rule?.lastupdatetime)
          ).toLocaleString(),
        })
      )
    : [];

  useEffect(() => {
    if (vehicleId) {
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          vehicleAlertsSingle: {
            ...singleVehicleAlertsQueryParams,
            deviceid: vehicleId,
          },
        })
      );
    }
  }, [vehicleId]);

  // fetch single vehicle alert details
  const {
    data: dataSingleVehicleAlert,
    isFetching: isFetchingSingleVehicleAlert,
  } = useSingleOrganizationVehicleAlertQuery(singleVehicleAlertsQueryParams, {
    skip: !vehicleId,
  });

  const { results: singleVehicleAlertResults, count: singleVehicleAlertCount } =
    dataSingleVehicleAlert || {};

  // preparing table data
  const tableData: TAdminTableRowData[] = !!singleVehicleAlertResults
    ? (singleVehicleAlertResults || ([] as TLegacySingleAlert[])).map(
        (item: TLegacySingleAlert, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.vehicle_alert}/${item.deviceid}`,
          cellData: [
            item?.deviceid ?? "-", // "Device Id",
            item?.rule?.description ?? "-", // "Description",
            <span className="text-field-success">{tMain("yes")}</span>, // "Alert Status",
            item?.rule?.ruleid ?? "-", // "Alert Rule"
            new Date(dbTimeToJsTime(item?.timestamp)).toLocaleString() ?? "-", // "Date and time",
          ],
        })
      )
    : [];

  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={() =>
          navigate(routeUrls.dashboardChildren.vehicle_alert)
        }
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${
              isFetchingOrgVehicleAlerts
                ? " opacity-40 pointer-events-none"
                : ""
            }`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={t("search_placeholder")}
              initialValue={orgVehicleAlertsQueryParams.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {listData?.map((item: any, index: number) => {
                // Find the matching vehicle details from list
                const matchedVehicle = allVehiclesData?.results?.find(
                  (v: any) => v.deviceid === item.device_id
                );

                // 5. Format the title
                const displayTitle = consistentDisplayAssetItem(
                  item.device_id,
                  matchedVehicle?.description,
                  matchedVehicle?.displayname,
                  matchedVehicle?.drivername,
                  showDriver
                );

                return (
                  <AdminListingColumnItem
                    key={index}
                    selected={vehicleId === item.device_id}
                    onClick={() =>
                      navigate(
                        `${routeUrls.dashboardChildren.vehicle_alert}/${item.device_id}`
                      )
                    }
                    title={displayTitle}
                    description={item.description}
                    asideText={""}
                    bottomText={item.last_alert}
                  />
                );
              })}
            </div>
            {!isFetchingOrgVehicleAlerts && (
              <Pagination
                pageSize={orgVehicleAlertsQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      vehicleAlerts: {
                        ...orgVehicleAlertsQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count
                    ? Math.ceil(count / orgVehicleAlertsQueryParams.page_size)
                    : 1
                }
                forcePage={orgVehicleAlertsQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      vehicleAlerts: {
                        ...orgVehicleAlertsQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div className="col-span-12 xl:col-span-9">
            <div className="py-4 mt-8">
              <AdminTable
                customTableHeadStyles="bg-blue-100" //TODO "bg-accent-blue-paleO66"  is not affecting here so put bg-blue-200
                columns={all_columns}
                data={tableData}
                isLoading={isFetchingSingleVehicleAlert}
                listingWrapperRef={listingWrapperRef}
                viewportHeightOffset={"400"}
              />
              {!isFetchingSingleVehicleAlert && (
                <Pagination
                  pageSize={singleVehicleAlertsQueryParams.page_size}
                  handlePageSizeChange={(e: TSelectboxOption | null) => {
                    dispatch(
                      setListingQueryParams({
                        ...listingQueryParams,
                        vehicleAlertsSingle: {
                          ...singleVehicleAlertsQueryParams,
                          page: 1,
                          page_size: parseInt(`${e?.value}`),
                        },
                      })
                    );
                  }}
                  totalPages={
                    singleVehicleAlertCount
                      ? Math.ceil(
                          singleVehicleAlertCount /
                            singleVehicleAlertsQueryParams.page_size
                        )
                      : 1
                  }
                  forcePage={singleVehicleAlertsQueryParams.page - 1}
                  handlePageClick={(data: TPaginationSelected) => {
                    dispatch(
                      setListingQueryParams({
                        ...listingQueryParams,
                        vehicleAlertsSingle: {
                          ...singleVehicleAlertsQueryParams,
                          page: data?.selected + 1,
                        },
                      })
                    );
                  }}
                  paginCurrentPage={singleVehicleAlertsQueryParams.page}
                  paginItemsOnCurrentPage={
                    singleVehicleAlertResults?.length || 0
                  }
                  paginTotalItemsCount={singleVehicleAlertCount}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScreenReportAlertDetails;
